import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { AVAILABILITY_TAB_KEY } from '../tabs/availability/tab_config'

const EDITOR_PREVIEW_MODES = {
  default: 'default',
  calendar: 'calendar',
}

const EditorPreviewStateContext = createContext({
  mode: EDITOR_PREVIEW_MODES.default,
  calendarFormPath: null,
})

const EditorPreviewActionsContext = createContext({
  showAvailabilityCalendar: () => {},
  toggleAvailabilityCalendar: () => {},
  showDefaultPreview: () => {},
})

export const EditorPreviewProvider = ({ activeTabKey, children }) => {
  const [previewState, setPreviewState] = useState({
    mode: EDITOR_PREVIEW_MODES.default,
    calendarFormPath: null,
  })

  const showDefaultPreview = useCallback(() => {
    setPreviewState({
      mode: EDITOR_PREVIEW_MODES.default,
      calendarFormPath: null,
    })
  }, [])

  const showAvailabilityCalendar = useCallback((formPath) => {
    setPreviewState({
      mode: EDITOR_PREVIEW_MODES.calendar,
      calendarFormPath: formPath,
    })
  }, [])

  const toggleAvailabilityCalendar = useCallback((formPath) => {
    setPreviewState((current) => {
      const isShowingSameCalendar = (
        current.mode === EDITOR_PREVIEW_MODES.calendar
        && current.calendarFormPath === formPath
      )

      if (isShowingSameCalendar) {
        return {
          mode: EDITOR_PREVIEW_MODES.default,
          calendarFormPath: null,
        }
      }

      return {
        mode: EDITOR_PREVIEW_MODES.calendar,
        calendarFormPath: formPath,
      }
    })
  }, [])

  useEffect(() => {
    if (activeTabKey !== AVAILABILITY_TAB_KEY) {
      showDefaultPreview()
    }
  }, [activeTabKey, showDefaultPreview])

  const state = useMemo(() => previewState, [previewState])

  const actions = useMemo(() => ({
    showAvailabilityCalendar,
    toggleAvailabilityCalendar,
    showDefaultPreview,
  }), [showAvailabilityCalendar, toggleAvailabilityCalendar, showDefaultPreview])

  return (
    <EditorPreviewStateContext.Provider value={state}>
      <EditorPreviewActionsContext.Provider value={actions}>
        {children}
      </EditorPreviewActionsContext.Provider>
    </EditorPreviewStateContext.Provider>
  )
}

export const useEditorPreviewState = () => useContext(EditorPreviewStateContext)

export const useEditorPreviewActions = () => useContext(EditorPreviewActionsContext)

export { EDITOR_PREVIEW_MODES }
